<template>
  <div class="page-overall-submission-denied">
    <div v-if="loading" class="loading-container">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <div v-else>
      <!-- banner remaining quota -->
      <CardRemainingEvidenceSubmission
        v-if="quotaEmpty"
        id="card-remaining-evidence-submission"
        :learning-unit="learningUnit"
      />
      <b-container class="container__overall-denied">
        <div class="wrapper__overall-denied">
          <!-- button back to method components -->
          <div>
            <b-button
              ref="btn__previous-page"
              variant="link"
              class="btn__previous-page p-0 mb-3"
              @click="backToMethodComponents"
            >
              <font-awesome-icon icon="chevron-left" class="mr-u1" />
              <span> Back to method components </span>
            </b-button>
          </div>

          <!-- evidence submission user status v2 -->
          <EvidenceSubmissionUserStatusV2
            v-if="user && learningUnit"
            class="mb-3"
            :user="user"
            :learning-unit="learningUnit"
            view="learner"
            :selected-evidence-submission-id="selectedEvidenceSubmissionId"
          />
          <!-- learning unit evaluation score card -->
          <LearningUnitEvaluationScoreCard
            v-if="
              learningUnit &&
              (passedPosts.length ||
                deniedPosts.length ||
                (this.evidenceSubmission && this.evidenceSubmission.status == 'DENIED'))
            "
            class="mb-3"
            :learning-unit="learningUnit"
            :passed-posts="passedPosts.length"
            :denied-posts="deniedPosts.length"
            :quota-empty="quotaEmpty"
            :is-updating="isUpdating"
            @update-submission="handleUpdateSubmission"
          />

          <!-- learning unit score description card -->
          <LearningUnitScoreDescriptionCard
            v-if="showResult && learningUnit && learningUnit.score_description"
            class="mb-3 card__score-description"
            :learning-unit="learningUnit"
          />

          <!-- post  -->
          <PostEvaluationAssessmentPart
            v-for="(partMeta, index) in showResult ? partMetaData : []"
            :key="`card__part-${index + 1}-task-list`"
            :learning-unit="learningUnit"
            :evidence-submission="resultEvidenceSubmission"
            :part="partMeta"
            :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
            assessable
            submittable
            :is-denied-page="true"
            disabled
            :assessable-title="'Feedback from assessor'"
          />

          <!-- evidence submission next steps -->
          <EvidenceSubmissionNextSteps
            v-if="!isProjectEnded && (deniedPosts.length || passedPosts.length)"
            :denied-posts="deniedPosts"
            :passed-posts="passedPosts"
            :quota-empty="quotaEmpty"
            :loading="isUpdating"
            @update-submission="handleUpdateSubmission"
            @buy-quota="scrollToCardRemainingEvidenceSubmission"
          />
        </div>
      </b-container>
    </div>
  </div>
</template>

<script>
import { usePostExtractor } from '@/composables/usePostExtractor';
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import Post from '@/models/Post';
import PostResponse from '@/models/PostResponse';
import Application from '@/models/Application';
import User from '@/models/User';

import EvidenceSubmissionUserStatusV2 from '@/components/EvidenceSubmission/EvidenceSubmissionUserStatusV2';
import EvidenceSubmissionNextSteps from '@/components/EvidenceSubmission/EvidenceSubmissionNextSteps.vue';
import CardRemainingEvidenceSubmission from '@/components/EvidenceSubmission/CardRemainingEvidenceSubmission.vue';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard.vue';
import LearningUnitEvaluationScoreCard from '@/components/LearningUnit/LearningUnitEvaluationScoreCard.vue';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';

import { userInfoMixin } from '@/mixins';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

library.add(faChevronLeft);

export default {
  components: {
    EvidenceSubmissionUserStatusV2,
    EvidenceSubmissionNextSteps,
    CardRemainingEvidenceSubmission,
    LearningUnitScoreDescriptionCard,
    LearningUnitEvaluationScoreCard,
    PostEvaluationAssessmentPart,

    FontAwesomeIcon
  },
  mixins: [userInfoMixin],
  data() {
    return {
      loading: true,
      selectedEvidenceSubmissionId: this.$route.query.submissionId || null,
      passedPosts: [],
      deniedPosts: [],
      showCardRemaining: false,
      isUpdating: false
    };
  },
  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.query()
        .with('ultimate_learning_outcome')
        .whereId(this.learningUnitId)
        .first();
    },

    evidenceSubmission() {
      return this.selectedEvidenceSubmissionId
        ? EvidenceSubmission.query().with('post_responses').find(this.selectedEvidenceSubmissionId)
        : null;
    },

    isProjectEnded() {
      // The selected (latest) submission is the project-ended one: denied with
      // no submitted date. It carries no result of its own.
      return Boolean(
        this.evidenceSubmission &&
          this.evidenceSubmission.isDenied &&
          !this.evidenceSubmission.submitted_at
      );
    },

    resultEvidenceSubmission() {
      // When the project has ended, fall back to the previous submission (the
      // last one the assessor actually reviewed) so we can show its feedback.
      // Otherwise show the selected submission's own result.
      const resultId = this.isProjectEnded
        ? this.evidenceSubmission?.previous_evidence_submission
        : this.selectedEvidenceSubmissionId;

      return resultId
        ? EvidenceSubmission.query().with('post_responses').find(resultId)
        : null;
    },

    showResult() {
      return Boolean(this.resultEvidenceSubmission?.assessor_reviewed_at);
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnitId)
        .with('post_responses')
        .get();

      return posts;
    },

    partMetaData() {
      return Object.keys(Post.PART).filter((part) => this.partPostIds[part]?.length > 0);
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },
    quotaEmpty() {
      return (
        this.user &&
        this.user.applications.find((app) => app.learning_unit === this.learningUnitId)
          ?.remaining_submission === 0
      );
    },
    application() {
      return Application.query()
        .where((apply) => {
          return apply.user.id === this.userId && apply.learning_unit.id === this.learningUnitId;
        })
        .first();
    },

    remainingSubmission() {
      return this.application ? this.application.remaining_submission || 0 : 0;
    },

    isLearner() {
      return (
        !this.$currentUser.isRole('issuer') &&
        !this.$currentUser.isLearningUnitMemberOf(this.learningUnitId)
      );
    },

    shouldShowCardRemaining() {
      return (
        this.isLearner &&
        this.application &&
        this.remainingSubmission === 0 &&
        this.learningUnit &&
        this.showCardRemaining
      );
    }
  },
  created() {
    this.fetch();
  },
  methods: {
    backToMethodComponents() {
      this.$router.push({
        name: 'learning-unit/method-components',
        params: {
          learningUnitId: this.$route.params.learningUnitId
        }
      });
    },
    async fetch() {
      this.loading = true;

      try {
        await User.api().fetchAuth();
        await Post.api().fetchEvaluationAssessments({
          learningUnitId: this.learningUnitId
        });

        await EvidenceSubmission.api().fetchLearner(this.learningUnitId);

        const latestEvidenceSubmission = await EvidenceSubmission.getLatestEvidenceSubmission(
          this.learningUnitId,
          this.userId
        );

        //Only latest evidence submission is denied, can access this page
        if (latestEvidenceSubmission && latestEvidenceSubmission.status === 'DENIED') {
          this.selectedEvidenceSubmissionId = latestEvidenceSubmission.id;
        } else {
          this.backToMethodComponents();
        }

        // Calculate scores after data is loaded
        this.getAllScore();
      } catch (error) {
        this.$toast('Cannot load your submissions, please try again');
      } finally {
        setTimeout(() => {
          this.loading = false;
          this.showCardRemaining = true;
          this.$emit('fetch-complete');
        }, 500);
      }
    },
    getAllScore() {
      if (!this.resultEvidenceSubmission) {
        return;
      }

      const allScores = PostResponse.query()
        .where('evidence_submission_id', this.resultEvidenceSubmission.id)
        .with('assessment_evaluation')
        .with('post')
        .get()
        .filter((response) => response.assessment_evaluation !== null);

      const passed = allScores.filter(
        (postResponse) => postResponse.assessment_evaluation?.pass === true
      );

      const denied = allScores.filter(
        (postResponse) => postResponse.assessment_evaluation?.pass === false
      );

      this.$set(this, 'passedPosts', passed);
      this.$set(this, 'deniedPosts', denied);
    },

    async handleUpdateSubmission() {
      // Prevent double-click execution
      if (this.isUpdating) {
        return;
      }

      // If no remaining submission, scroll to buy quota card
      if (this.remainingSubmission === 0) {
        this.$nextTick(() => {
          const cardElement = this.$refs.cardRemainingSubmission?.$el;
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
        return;
      }

      this.isUpdating = true;
      try {
        // Use save: false to prevent store update and quota count flash
        await EvidenceSubmission.api().post('/evidence-submissions/resubmit', {
          learning_unit: this.learningUnitId
        }, { save: false });

        /* delete oldest evidence due to have to show only 2 evidences */
        const evidenceSubmissions = EvidenceSubmission.query()
          .where({
            user_id: this.userId,
            learning_unit_id: this.learningUnitId
          })
          .orderBy('submitted_no')
          .get();

        if (evidenceSubmissions.length > 2) {
          EvidenceSubmission.delete(evidenceSubmissions[0].id);
        }

        this.$toast.success('Create new submission successfully');

        // Navigate to the submission page
        this.$router.push({
          name: 'learning-unit/verify',
          query: {
            tab: 'submission'
          },
          params: {
            learningUnitId: this.learningUnitId
          }
        });
      } catch (error) {
        console.error(error);
        this.$toast.error('Cannot submit your evidence, please try again');
      } finally {
        this.isUpdating = false;
      }
    },
    scrollToCardRemainingEvidenceSubmission() {
      this.$nextTick(() => {
        const element = document.getElementById('card-remaining-evidence-submission');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.page-overall-submission-denied {
  background-color: #eceef7;
  width: 100%;
  height: 100%;
  margin-top: -24px;

  min-height: calc(100dvh - 72px);
}

.wrapper__overall-denied {
  max-width: 910px;
  display: flex;
  flex-direction: column;
  margin: 20px auto;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
  width: 100%;
}

@media screen and (min-width: 1200px) {
  .page-overall-submission-denied {
    min-height: calc(100dvh - 76px);
  }
}
</style>
